import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { FC, ReactElement } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { createActivity } from "../../api/activity/createActivity";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateActivityRequest } from "../../types/model/activity";

interface ActivityQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  onSuccess?: (activityId: number) => void;
}

const ActivityQuickCreateDialog: FC<ActivityQuickCreateDialogProps> = ({
  open,
  onClose,
  projectId,
  onSuccess,
}): ReactElement => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const formStyles = useFormStyles();

  const handleSubmit = async (values: any) => {
    dispatch(setLoading(true));

    const session = JSON.parse(localStorage.getItem("session") || "{}");
    const ownerId = session?.user?.id ?? session?.userId;

    const request: CreateActivityRequest = {
      name: values.name,
      description: values.description,
      ...(values.startDate ? { startDate: values.startDate } : {}),
      ...(values.endDate ? { endDate: values.endDate } : {}),
      managerId: ownerId,
      accountId: Number(session?.accountId),
      ownerId,
    };

    try {
      const response = await createActivity(request, projectId);
      enqueueSnackbar(t("activities.created"), { variant: "success" });

      if (onSuccess) {
        onSuccess(response.id);
      }

      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("activities.create")}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Formik
          initialValues={{
            name: "",
            description: "",
            startDate: "",
            endDate: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            name: Yup.string().required(t("activities.name-required")),
            description: Yup.string().required(t("activities.description-required")),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            } = props;

            return (
              <Box component="form" onSubmit={handleSubmit}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <TextField
                      fullWidth
                      required
                      id="name"
                      label={t("generic.name")}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.name && touched.name)}
                      helperText={errors.name ? errors.name : ""}
                      sx={{ ...formStyles, mt: 1 }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <TextField
                      fullWidth
                      required
                      id="description"
                      label={t("generic.description")}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.description && touched.description)}
                      helperText={
                        errors.description ? errors.description : ""
                      }
                      multiline
                      rows={3}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: 600, mb: 1, mt: 1.5 }}
                    >
                      {t("projects.dates")} ({t("generic.optional")})
                    </Typography>

                    <Grid2 container spacing={2}>
                      <Grid2 size={6}>
                        <DatePicker
                          label={t("activities.start-date")}
                          value={values.startDate ? moment(values.startDate) : null}
                          onChange={(date) =>
                            setFieldValue("startDate", date ? date.toISOString() : "")
                          }
                          slotProps={{
                            field: { clearable: true },
                            textField: {
                              fullWidth: true,
                              error: !!(errors.startDate && touched.startDate),
                              helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                              sx: { ...formStyles },
                            },
                          }}
                          format="DD/MM/YYYY"
                          views={["day", "month", "year"]}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <DatePicker
                          label={t("activities.end-date")}
                          value={values.endDate ? moment(values.endDate) : null}
                          onChange={(date) =>
                            setFieldValue("endDate", date ? date.toISOString() : "")
                          }
                          slotProps={{
                            field: { clearable: true },
                            textField: {
                              fullWidth: true,
                              error: !!(errors.endDate && touched.endDate),
                              helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                              sx: { ...formStyles },
                            },
                          }}
                          format="DD/MM/YYYY"
                          views={["day", "month", "year"]}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <DialogActions sx={{ mt: 3, px: 0 }}>
                  <Button onClick={onClose} color="secondary">{t("generic.cancel")}</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={isSubmitting}
                  >
                    {t("generic.create")}
                  </Button>
                </DialogActions>
              </Box>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityQuickCreateDialog;
